import React, { useState } from 'react'
import { HiChevronDown, HiChevronUp, HiOutlineDocumentText, HiOutlineCog, HiOutlineLightningBolt } from 'react-icons/hi'

interface CalculationDetailsProps {
  theme?: 'dark' | 'light'
}

export default function CalculationDetails({ theme = 'dark' }: CalculationDetailsProps) {
  const [open, setOpen] = useState({ general: true, formulas: false, recommendation: false })

  const toggle = (k: string) =>
    setOpen(prev => ({
      ...prev,
      [k]: !prev[k] 
    }))

  const cardBase = 'w-full max-w-4xl mx-auto rounded-2xl p-5 md:p-6 shadow-md transition-colors duration-200'
  const bg = theme === 'dark' ? 'bg-[#0f1724] text-[#e6eef8]' : 'bg-white text-[#0f1724]'
  const border = theme === 'dark' ? 'border border-[#223042]' : 'border border-[#dcdcdc]'
  const formulaBg = theme === 'dark' ? 'bg-black/30' : 'bg-gray-100 text-gray-800'

  return (
    <section className='calculation-details py-8' dir='rtl'>
      <div className='container px-4'>
        <article className={`${cardBase} ${bg} ${border} mb-5`}>
          <header className='flex items-start justify-between cursor-pointer' onClick={() => toggle('general')}>
            <div className='flex items-center gap-3'>
              <div className='p-2 rounded-xl bg-gradient-to-r from-sky-500 to-pink-500 text-white'>
                <HiOutlineDocumentText className='w-5 h-5' />
              </div>
              <div>
                <h3 className='text-base font-medium'>این محاسبات بر چه اساسی انجام می‌شوند؟</h3>
                <p className='text-xs opacity-70 mt-1'>توضیح عمومی منطق پشت صحنه</p>
              </div>
            </div>
            <button>{open.general ? <HiChevronUp /> : <HiChevronDown />}</button>
          </header>

          {open.general && (
            <div className='mt-4 text-sm leading-7 opacity-90'>
              <p>این سیستم بر اساس شبیه‌سازی آماری (Monte Carlo) با ۱۲۰۰ مسیر احتمالی طراحی شده است. منطق اصلی شامل موارد زیر است:</p>

              <ul className='list-inside list-disc space-y-2 text-xs md:text-sm mt-3 opacity-80'>
                <li>
                  <strong>مدیریت سرمایه Kelly:</strong> استفاده از فرمول کسری کِلی با ضریب ثابت ۰.۷۵ (برای کاهش نوسانات).
                </li>
                <li>
                  <strong>کارمزد واقعی:</strong> کسر مبلغ ثابت ۰.۰۱ دلار از هر تراکنش (به عنوان کمیسیون یا اسپرد).
                </li>
                <li>
                  <strong>رشد مرکب:</strong> سودها در حساب باقی می‌مانند و حجم معاملات بعدی بر اساس سرمایه جدید محاسبه می‌شود.
                </li>
                <li>
                  <strong>تحلیل آماری:</strong> محاسبه سه سناریوی «بدبینانه» (۵٪)، «نرمال» (۵۰٪) و «خوش‌بینانه» (۹۵٪) از بین ۱۲۰۰ حالت ممکن.
                </li>
              </ul>
            </div>
          )}
        </article>
        <article className={`${cardBase} ${bg} ${border} mb-5`}>
          <header className='flex items-start justify-between cursor-pointer' onClick={() => toggle('formulas')}>
            <div className='flex items-center gap-3'>
              <div className='p-2 rounded-xl bg-gradient-to-r from-amber-400 to-rose-500 text-white'>
                <HiOutlineCog className='w-5 h-5' />
              </div>
              <div>
                <h3 className='text-base font-medium'>فرمول‌های دقیق ریاضی</h3>
                <p className='text-xs opacity-70 mt-1'>مطابق با کد شبیه‌سازی استفاده شده</p>
              </div>
            </div>
            <button>{open.formulas ? <HiChevronUp /> : <HiChevronDown />}</button>
          </header>

          {open.formulas && (
            <div className='mt-4 text-sm leading-7 opacity-90'>
              <div className='grid grid-cols-1 gap-5'>
                
                <div>
                  <h4 className='font-medium mb-2 text-sky-500'>۱. محاسبه ضریب کِلی (Kelly Fraction)</h4>
                  <div className={`${formulaBg} p-3 rounded-lg text-left dir-ltr font-mono text-xs md:text-sm space-y-2`}>
                     <p>fStar = p - (1 - p) / RR</p>
                     <p className='text-green-500'>// Fixed Alpha = 0.75</p>
                     <p>f = clamp(fStar, 0, 1) * 0.75</p>
                  </div>
                  <p className='text-xs mt-1 opacity-70'>که در آن p درصد برد و RR نسبت ریسک به ریوارد است.</p>
                </div>

                <div>
                  <h4 className='font-medium mb-2 text-sky-500'>۲. ضرایب رشد سرمایه (Multipliers)</h4>
                  <div className={`${formulaBg} p-3 rounded-lg text-left dir-ltr font-mono text-xs md:text-sm space-y-2`}>
                     <p className='text-gray-400'>// Win Multiplier</p>
                     <p>Mw = 1 + (f * R * RR)</p>
                     <p className='text-gray-400 mt-2'>// Loss Multiplier</p>
                     <p>Ml = 1 - (f * R)</p>
                  </div>
                  <p className='text-xs mt-1 opacity-70'>متغیر R همان ریسک هر ترید (مثلا ۰.۰۱) است که توسط ضریب کلی (f) تعدیل می‌شود.</p>
                </div>

                <div>
                  <h4 className='font-medium mb-2 text-sky-500'>۳. آپدیت موجودی (در هر ترید)</h4>
                  <div className={`${formulaBg} p-3 rounded-lg text-left dir-ltr font-mono text-xs md:text-sm space-y-2`}>
                     <p>if (Win) V = V * Mw</p>
                     <p>else     V = V * Ml</p>
                     <p className='text-yellow-500'>V = V - 0.01  </p>
                  </div>
                </div>

              </div>
            </div>
          )}
        </article>

        <article className={`${cardBase} ${bg} ${border}`}>
          <header className='flex items-start justify-between cursor-pointer' onClick={() => toggle('recommendation')}>
            <div className='flex items-center gap-3'>
              <div className='p-2 rounded-xl bg-gradient-to-r from-green-400 to-blue-500 text-white'>
                <HiOutlineLightningBolt className='w-5 h-5' />
              </div>
              <div>
                <h3 className='text-base font-medium'>منطق امتیازدهی و پیشنهاد</h3>
                <p className='text-xs opacity-70 mt-1'>نحوه انتخاب بهترین پلن</p>
              </div>
            </div>
            <button>{open.recommendation ? <HiChevronUp /> : <HiChevronDown />}</button>
          </header>

          {open.recommendation && (
            <div className='mt-4 text-sm leading-7 opacity-90'>
              <p className='mb-3'>سیستم تمام ترکیبات ممکن (WinRate, RR, تعداد ترید) را بررسی کرده و بر اساس فرمول زیر به هر پلن امتیاز می‌دهد:</p>
              
              <div className={`${formulaBg} p-4 rounded-lg text-left dir-ltr font-mono text-xs md:text-sm mb-4`}>
                <p>Edge = max(0, MedianReturn - Target)</p>
                <p className='mt-2'>Score = (0.6 * ProbHit) + <br/>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;(0.3 * Edge) + <br/>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;(0.1 * WorstCaseReturn)</p>
              </div>

              <ul className='list-inside list-decimal space-y-2 text-xs md:text-sm opacity-80'>
                <li>
                  <strong>ProbHit (وزن ۶۰٪):</strong> مهم‌ترین فاکتور؛ احتمال اینکه کاربر در پایان دوره به سود هدف برسد.
                </li>
                <li>
                  <strong>Edge (وزن ۳۰٪):</strong> میزان سودی که پلن به طور میانگین (Median) فراتر از هدف کاربر می‌سازد.
                </li>
                <li>
                  <strong>WorstCase (وزن ۱۰٪):</strong> بازدهی در بدترین سناریوی ۵ درصد؛ برای اطمینان از ایمنی حساب.
                </li>
                <li className='text-rose-400'>
                  <strong>فیلتر ایمنی:</strong> هر پلنی که در بدترین حالت (۵٪) بیش از ۵ درصد ضرر (Drawdown) داشته باشد، حذف می‌شود.
                </li>
              </ul>
            </div>
          )}
        </article>
      </div>
    </section>
  )
}